import { useNavigate } from "react-router-dom";
import { ShieldCheck, Check, AlertCircle, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { ServicesGrid, HowItWorks, FaqAccordion, WhatsAppButton, SectionHeading } from "@/components/sections";
import { Button } from "@/components/ui/button";
import { site } from "@/config/site";
import { getCampaign } from "@/data/campaigns";
import { trackEvent, EVENTS } from "@/lib/analytics";

const GENERIC_FAQS = [
  { q: "How does the consultation process work?", a: "You complete a short assessment, your information is reviewed, and a consultation can be arranged to discuss the right support for you." },
  { q: "How are fees determined?", a: "Solutions are tailored according to the services, requirements and complexity of each client. Fees are discussed during consultation." },
  { q: "Can Bonsai assist if my records are behind?", a: "Yes. We regularly help clients bring their accounting records up to date." },
  { q: "Can Bonsai assist clients across South Africa?", a: "Yes, we work with clients in various provinces and can support you remotely where appropriate." },
];

export default function CampaignLanding({ slug }) {
  const navigate = useNavigate();
  const c = getCampaign(slug);
  if (!c) return null;

  const startAssessment = () => {
    trackEvent(EVENTS.CONSULTATION_REQUEST, { from: `campaign:${slug}` });
    const params = c.industryPreset ? `?industry=${encodeURIComponent(c.industryPreset)}` : "";
    navigate(`/assessment${params}`);
  };

  return (
    <>
      <Seo title={c.eyebrow} description={c.subhead} path={`/${slug}`} />

      {/* Hero */}
      <section className="bg-[#133827] text-white">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-[#C68B59]">
            <ShieldCheck className="h-4 w-4" /> {site.credentials.trustLine}
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl">
            {c.headline}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">{c.subhead}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button onClick={startAssessment} data-testid="campaign-consult-btn" className="h-12 bg-[#C68B59] px-6 text-base text-white hover:bg-[#b2794a]">
              Request a Consultation <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <WhatsAppButton context="consultation" testId="campaign-whatsapp-btn" className="h-12 bg-white/5 px-6 text-base" />
          </div>
        </div>
      </section>

      {/* Pains */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Sound Familiar?" title="Common challenges we help with" />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {c.pains.map((p, i) => (
              <div key={i} data-testid={`campaign-pain-${i}`} className="rounded-2xl border border-slate-200/80 bg-white p-7">
                <AlertCircle className="h-6 w-6 text-[#C68B59]" />
                <p className="mt-4 text-sm leading-relaxed text-[#18201B]">{p}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex items-center gap-2 text-sm text-[#526057]">
            <Check className="h-5 w-5 text-[#2C5E43]" /> Bonsai Financial handles this so you can focus on your work.
          </div>
        </div>
      </section>

      <ServicesGrid withHeading filterIds={c.serviceIds} />
      <HowItWorks />

      {/* FAQ + CTA */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-2xl font-bold text-[#18201B] sm:text-3xl">Questions</h2>
          <FaqAccordion items={GENERIC_FAQS} />
          <div className="mt-10 text-center">
            <Button onClick={startAssessment} data-testid="campaign-bottom-consult-btn" className="h-12 bg-[#133827] px-6 text-base text-white hover:bg-[#0d261a]">
              Start Your Assessment <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
